import React from "react";
import { useTranslation } from "react-i18next";
import {
  Identifiable,
  project,
  resource,
  Group as G,
  WorkLoadsCalendar,
} from "@planzone/dto";
import {
  useProjectResourcesRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveResourceGroups,
} from "@planzone/view-models";
import { useWorkspaceContext } from "@scenes/workspaceContext";

import { Filter } from "@common/filter";
import { ToggleInput } from "@common/toggleInput";
import { Context } from "../context";
import { sortWorkLoads } from "../utils";
import { Props } from "./types";
import { Resource } from "./resource";
import { GroupIcon } from "./groupIcon";
import { Group } from "./group";
import { Loader } from "./loader";
import styles from "./styles.scss";

function Resources(props: Props) {
  const { t } = useTranslation();
  const { cell, areWorkloadsGroupsToggled, toggleWorkloadsGroups } =
    React.useContext(Context);
  const { planzone } = useRetrieveCurrentPlanzone();
  const ctx = useWorkspaceContext();
  const isInProject = ctx.type === "project";
  const {
    isRequestPending: isPlanzoneRequestPending,
    isRequestSuccess: isPlanzoneRequestSuccess,
    isRequestFailure: isPlanzoneRequestFailure,
    groups,
    retrieveResourceGroups: retrievePlanzoneResourceGroups,
  } = useRetrieveResourceGroups(planzone.uuid);
  const {
    isRequestPending: isProjectRequestPending,
    isRequestSuccess: isProjectRequestSuccess,
    isRequestFailure: isProjectRequestFailure,
    groups: projectGroups,
    retrieveResourceGroups: retrieveProjectResourceGroups,
  } = useRetrieveResourceGroups(isInProject ? ctx.project.uuid : project.uuid);
  const { projectResources } = useProjectResourcesRetrieval(
    isInProject ? ctx.project : project,
  );
  const planzoneGroups = isInProject
    ? groups.reduce<Identifiable<G>[]>((filteredGroups, group) => {
        const filteredGroup = {
          ...group,
          resources: group.resources.filter(
            ({ uuid }) =>
              !!projectResources.find(({ resource }) => resource.uuid === uuid),
          ),
        };

        return filteredGroup.resources.length > 0
          ? [...filteredGroups, filteredGroup]
          : filteredGroups;
      }, [])
    : groups;

  React.useEffect(() => {
    !isPlanzoneRequestPending &&
      !isPlanzoneRequestSuccess &&
      !isPlanzoneRequestFailure &&
      retrievePlanzoneResourceGroups({
        namespace: planzone.uuid,
        resourceType: "planzone",
      });
  }, []);

  React.useEffect(() => {
    !isProjectRequestPending &&
      !isProjectRequestSuccess &&
      !isProjectRequestFailure &&
      retrieveProjectResourceGroups({
        namespace: isInProject ? ctx.project.uuid : project.uuid,
        resourceType: "project",
      });
  }, []);

  return (
    <div className={styles.resources}>
      <div className={styles.header}>
        <div className={styles.row}>
          <p className={styles.label}>{t("workLoads.title")}</p>
          <div className={styles.actions} onClick={toggleWorkloadsGroups}>
            <GroupIcon className={styles.icon} />
            <p className={styles.label}>{t("workLoads.labels.groups")}</p>
            <ToggleInput isToggled={areWorkloadsGroupsToggled} />
          </div>
        </div>
        <div className={styles.row}>
          <p className={styles.label}>{t("workLoads.labels.name")}</p>
        </div>
      </div>
      {props.isLoading ? (
        <Loader />
      ) : areWorkloadsGroupsToggled ? (
        <>
          {planzoneGroups
            .reduce<(Identifiable<G> & { workloads: WorkLoadsCalendar })[]>(
              (nextGroups, group) => [
                ...nextGroups,
                {
                  ...group,
                  workloads: sortWorkLoads(
                    group.resources.reduce<WorkLoadsCalendar>(
                      (workloads, resource) => {
                        const workload = props.workLoads.find(
                          ({ uuid }) => uuid === resource.uuid,
                        );

                        return workload ? [...workloads, workload] : workloads;
                      },
                      [],
                    ),
                  ),
                },
              ],
              [],
            )
            .map((group) => (
              <Group key={group.uuid} filter={props.filter} group={group} />
            ))}
          {projectGroups
            .reduce<(Identifiable<G> & { workloads: WorkLoadsCalendar })[]>(
              (nextGroups, group) => [
                ...nextGroups,
                {
                  ...group,
                  workloads: sortWorkLoads(
                    group.resources.reduce<WorkLoadsCalendar>(
                      (workloads, { resource }) => {
                        const workload = props.workLoads.find(
                          ({ uuid }) => uuid === resource.uuid,
                        );

                        return workload ? [...workloads, workload] : workloads;
                      },
                      [],
                    ),
                  ),
                },
              ],
              [],
            )
            .map((group) => (
              <Group key={group.uuid} filter={props.filter} group={group} />
            ))}
        </>
      ) : (
        <Filter
          filter={props.filter}
          data={sortWorkLoads(props.workLoads)}
          extractCriteria={(workLoad) => ({
            resource: {
              ...resource,
              uuid: workLoad.uuid,
              name: workLoad.resourceName,
              profession: workLoad.resourceTitle || "",
              tags: workLoad.resourceTags.map((name) => ({ name })),
            },
          })}
        >
          {(workLoads) => (
            <>
              {workLoads.map((workLoad, index) => (
                <div
                  key={workLoad.uuid}
                  className={styles.resource}
                  style={{ height: cell.height }}
                >
                  <Resource workLoad={workLoad} />
                </div>
              ))}
            </>
          )}
        </Filter>
      )}
    </div>
  );
}

export { Resources };
